import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input, Button, message, Checkbox, Alert, Select, Collapse, Space, Typography } from 'antd';
import ActiveStatusFormItem from 'app/shared/components/Form/ActiveStatusFormItem';
import './CancellationFees.css';
import { CancellationFee } from 'app/shared/model/CancellationFee';
import { BookingSystem, Centre, School } from 'app/shared/model';
import {
  getDraftCancellationFeeProduct,
  getFormErrors,
  getCancellationFeesProductsData,
  getCancellationFeeById,
  getAccountCategories,
  getProductCategories,
} from '../selectors';
import { setFormErrors, UpdateDraftCancellationFeeProduct, loadDraftCancellationFeeProduct } from '../actions';
import * as api from 'app/shared/api';
import { DivisionPricingYear, isDraftPricingYear } from 'app/shared/api/getYears';
import { pickDefaultPricingYear } from 'app/shared/model/pickDefaultPricingYear';
import { getSelectedDivisionId } from 'app/shared/division/selectedDivisionStorage';

import { CancellationFeeSaveStage } from './CancellationFeeSaveProgress';
import { isExcludedCountry } from 'app/shared/locationExclusions';

const COMPLETED_DISPLAY_MS = 800;

interface AddEditCancellationFeeFormOwnProps {
  cancellationFeeId?: number;
  editMode?: boolean;
  onSuccess?: () => void | Promise<void>;
  onSaveStageChange?: (stage: CancellationFeeSaveStage) => void;
}

const CANCELLATION_FEE_PRODUCT_TYPE_ID = 7;

function normalizeCentreKey(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const match = /^CN(\d+)$/i.exec(trimmed);
  return match ? match[1] : trimmed;
}

function normalizeSchoolKey(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const match = /^(?:School-)?(\d+)$/i.exec(trimmed);
  return match ? match[1] : trimmed;
}

function centreKeysMatch(a: string | number, b: string | number): boolean {
  const left = normalizeCentreKey(a);
  const right = normalizeCentreKey(b);
  return left != null && right != null && left === right;
}

function schoolKeysMatch(a: string | number, b: string | number): boolean {
  const left = normalizeSchoolKey(a);
  const right = normalizeSchoolKey(b);
  return left != null && right != null && left === right;
}

function normalizeCentreKeys(values: (string | number)[]): string[] {
  return [...new Set(values.map(normalizeCentreKey).filter((id): id is string => id != null))];
}

function normalizeSchoolKeys(values: (string | number)[]): string[] {
  return [...new Set(values.map(normalizeSchoolKey).filter((id): id is string => id != null))];
}

const AddEditCancellationFeeForm: React.FC<AddEditCancellationFeeFormOwnProps> = ({
  cancellationFeeId,
  editMode,
  onSuccess,
  onSaveStageChange,
}) => {
  const dispatch = useDispatch<any>();
  const draftProduct = useSelector(getDraftCancellationFeeProduct) as CancellationFee | undefined;
  const formErrors = useSelector(getFormErrors);
  const accountCategories = useSelector(getAccountCategories) ?? [];
  const productCategories = useSelector(getProductCategories) ?? [];
  const cancellationFeeProducts = useSelector(getCancellationFeesProductsData);
  const feeFromSelectorRaw = useSelector((state: any) =>
    getCancellationFeeById(state, cancellationFeeId ?? 0)
  );

  const isNew = !editMode || cancellationFeeId == null || cancellationFeeId <= 0;

  const cancellationFeeProduct = useMemo(() => {
    if (isNew) return undefined;
    const allFees = cancellationFeeProducts ?? [];
    const feeFromList = cancellationFeeId
      ? allFees.find(f => Number(f.id) === Number(cancellationFeeId))
      : undefined;
    const feeFromSelectorItem = Array.isArray(feeFromSelectorRaw) ? feeFromSelectorRaw[0] : undefined;
    return feeFromList ?? feeFromSelectorItem;
  }, [isNew, cancellationFeeProducts, cancellationFeeId, feeFromSelectorRaw]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string>('');
  const [statusIsError, setStatusIsError] = useState(false);
  const [bookingSystems, setBookingSystems] = useState<BookingSystem[]>([]);
  const [isExclusionLoading, setIsExclusionLoading] = useState(false);
  const [initialExcludedSystems, setInitialExcludedSystems] = useState<string[]>([]);
  const [excludedSystemIds, setExcludedSystemIds] = useState<string[]>([]);
  const [centres, setCentres] = useState<Centre[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [countries, setCountries] = useState<{ id: string; name: string }[]>([]);
  const [selectedCentreIds, setSelectedCentreIds] = useState<(string | number)[]>([]);
  const [selectedSchoolIds, setSelectedSchoolIds] = useState<(string | number)[]>([]);
  const [pricingYears, setPricingYears] = useState<DivisionPricingYear[]>([]);

  const divisionId = getSelectedDivisionId();
  const didInitNewForm = React.useRef(false);

  const publishedPricingYears = React.useMemo(
    () => pricingYears.filter(py => !isDraftPricingYear(py)),
    [pricingYears]
  );

  const defaultPublishedPricingYear = React.useMemo(
    () =>
      pickDefaultPricingYear(
        publishedPricingYears.map(py => ({ ...py, isLocked: py.isLocked ?? false }))
      )?.year,
    [publishedPricingYears]
  );

  useEffect(() => {
    let isMounted = true;
    api
      .getAllBookingSystems()
      .then(systems => {
        if (isMounted) setBookingSystems(systems);
      })
      .catch((err: any) => {
        message.error(err?.message ?? 'Failed to load booking systems');
      });
    api
      .getAllCentres()
      .then(centres => {
        if (isMounted) setCentres(centres);
      })
      .catch((err: any) => {
        message.error(err?.message ?? 'Failed to load centres');
      });
    api
      .getAllSchools()
      .then(schools => {
        if (isMounted) setSchools(schools);
      })
      .catch((err: any) => {
        message.error(err?.message ?? 'Failed to load schools');
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!divisionId) {
      setPricingYears([]);
      return;
    }

    let isMounted = true;
    api
      .getPricingYearsByDivision(divisionId)
      .then(years => {
        if (isMounted) setPricingYears(years ?? []);
      })
      .catch(() => {
        if (isMounted) setPricingYears([]);
      });

    return () => {
      isMounted = false;
    };
  }, [divisionId]);

  useEffect(() => {
    let isMounted = true;
    api.getAllCountries()
      .then(c => {
        if (isMounted) setCountries(c);
      })
      .catch((err: any) => {
        // not critical
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const activeCentres = React.useMemo(
    () => centres.filter(centre => centre.active !== false),
    [centres]
  );

  const centreSchoolMap = React.useMemo(() => {
    return schools.reduce((map: Record<string, string[]>, school) => {
      const centreKey = normalizeCentreKey(school.centreId ?? school.centreCode ?? '');
      const schoolKey = normalizeSchoolKey(school.id ?? '');
      if (!centreKey || !schoolKey) return map;
      const current = map[centreKey] ?? [];
      if (!current.some(id => schoolKeysMatch(id, schoolKey))) {
        current.push(schoolKey);
      }
      map[centreKey] = current;
      return map;
    }, {} as Record<string, string[]>);
  }, [schools]);

  const centresByCountry = React.useMemo(() => {
    const countryNameByCode = (code?: string | null) => {
      if (!code) return undefined;
      const found = countries.find(c => String(c.id) === String(code));
      return found?.name;
    };
    return activeCentres.reduce((map: Record<string, Centre[]>, centre) => {
      const centreKey = normalizeCentreKey(centre.id);
      if (!centreKey || (centreSchoolMap[centreKey]?.length ?? 0) === 0) {
        return map;
      }
      const displayName = (centre.countryName && String(centre.countryName).trim())
        || countryNameByCode(centre.countryCode)
        || (centre.countryCode ? String(centre.countryCode).trim() : undefined)
        || 'Other';
      if (isExcludedCountry(displayName, centre.countryCode)) {
        return map;
      }
      const list = map[displayName] ?? [];
      list.push(centre);
      map[displayName] = list;
      return map;
    }, {} as Record<string, Centre[]>);
  }, [activeCentres, countries, centreSchoolMap]);

  const getSchoolIdsForCentre = React.useCallback(
    (centreId: string | number): string[] => {
      const centreKey = normalizeCentreKey(centreId);
      return centreKey ? (centreSchoolMap[centreKey] ?? []) : [];
    },
    [centreSchoolMap]
  );

  const setCountrySelection = (countryCentreIds: (string | number)[], checked: boolean) => {
    if (checked) {
      const nextCentreIds = Array.from(new Set([...selectedCentreIds, ...countryCentreIds]));
      const schoolIdsToAdd = countryCentreIds.flatMap(cid => getSchoolIdsForCentre(cid));
      const nextSchoolIds = Array.from(new Set([...selectedSchoolIds, ...schoolIdsToAdd]));
      updateSelection(nextCentreIds, nextSchoolIds);
      return;
    }
    const nextCentreIds = selectedCentreIds.filter(id => !countryCentreIds.some(cid => centreKeysMatch(cid, id)));
    const schoolIdsToRemove = countryCentreIds.flatMap(cid => getSchoolIdsForCentre(cid));
    const nextSchoolIds = selectedSchoolIds.filter(id => !schoolIdsToRemove.some(sid => schoolKeysMatch(sid, id)));
    updateSelection(nextCentreIds, nextSchoolIds);
  };

  const updateSelection = (nextCentreIds: (string | number)[], nextSchoolIds: (string | number)[]) => {
    const normalizedCentreIds = normalizeCentreKeys(nextCentreIds);
    const normalizedSchoolIds = normalizeSchoolKeys(nextSchoolIds);
    setSelectedCentreIds(normalizedCentreIds);
    setSelectedSchoolIds(normalizedSchoolIds);
    form.setFieldsValue({ centreIds: normalizedCentreIds, schoolIds: normalizedSchoolIds });
  };

  const handleCentreToggle = (centreId: string | number, checked: boolean) => {
    const schoolIds = getSchoolIdsForCentre(centreId);
    if (checked) {
      const nextCentreIds = Array.from(new Set([...selectedCentreIds, centreId]));
      const nextSchoolIds = Array.from(new Set([...selectedSchoolIds, ...schoolIds]));
      updateSelection(nextCentreIds, nextSchoolIds);
      return;
    }
    const nextCentreIds = selectedCentreIds.filter(id => !centreKeysMatch(id, centreId));
    const nextSchoolIds = selectedSchoolIds.filter(id => !schoolIds.some(sid => schoolKeysMatch(sid, id)));
    updateSelection(nextCentreIds, nextSchoolIds);
  };

  const handleSchoolToggle = (centreId: string | number, schoolId: string | number, checked: boolean) => {
    const schoolIds = getSchoolIdsForCentre(centreId);
    const isCentreSelected = selectedCentreIds.some(id => centreKeysMatch(id, centreId));
    if (isCentreSelected) {
      if (!checked) {
        const nextCentreIds = selectedCentreIds.filter(id => !centreKeysMatch(id, centreId));
        const nextSchoolIds = selectedSchoolIds.filter(id => !schoolIds.some(sid => schoolKeysMatch(sid, id)));
        updateSelection(nextCentreIds, nextSchoolIds);
      }
      return;
    }

    if (checked) {
      const nextSchoolIds = Array.from(new Set([...selectedSchoolIds, schoolId]));
      const allSelected = schoolIds.length > 0 && schoolIds.every(id => nextSchoolIds.some(selected => schoolKeysMatch(selected, id)));
      if (allSelected) {
        const nextCentreIds = Array.from(new Set([...selectedCentreIds, centreId]));
        updateSelection(nextCentreIds, nextSchoolIds);
        return;
      }
      updateSelection(selectedCentreIds, nextSchoolIds);
      return;
    }

    const nextSchoolIds = selectedSchoolIds.filter(id => !schoolKeysMatch(id, schoolId));
    updateSelection(selectedCentreIds, nextSchoolIds);
  };

  useEffect(() => {
    if (isNew) {
      if (didInitNewForm.current) return;
      didInitNewForm.current = true;

      dispatch(loadDraftCancellationFeeProduct(undefined));
      dispatch(UpdateDraftCancellationFeeProduct(undefined));
      setInitialExcludedSystems([]);
      setExcludedSystemIds([]);
      setIsExclusionLoading(false);
      form.resetFields();
      form.setFieldsValue({
        name: undefined,
        id: undefined,
        active: true,
        accountCategoryId: undefined,
        productCategoryId: undefined,
        pricingYear: defaultPublishedPricingYear,
        centreIds: [],
        schoolIds: [],
      });
      updateSelection([], []);
      setStatusMsg('');
      setStatusIsError(false);
      return;
    }

    didInitNewForm.current = false;

    const existing = cancellationFeeProduct;
    if (!existing) return;

    const centreIds = Array.isArray(existing.centreIds) && existing.centreIds.length > 0
      ? normalizeCentreKeys(existing.centreIds)
      : Array.isArray(existing.schools)
      ? normalizeCentreKeys(existing.schools.map(s => s.centreId).filter(Boolean) as (string | number)[])
      : [];
    const schoolIdsFromExisting = Array.isArray(existing.schoolIds) && existing.schoolIds.length > 0
      ? normalizeSchoolKeys(existing.schoolIds)
      : Array.isArray(existing.schools)
      ? normalizeSchoolKeys(existing.schools.map(s => s.schoolId).filter(Boolean) as (string | number)[])
      : [];
    form.setFieldsValue({
      name: existing.name ?? '',
      id: existing.id,
      active: existing.active ?? existing.isPublished ?? true,
      accountCategoryId: existing.accountCategoryId,
      productCategoryId: existing.productCategoryId,
      pricingYear: defaultPublishedPricingYear,
      centreIds,
      schoolIds: schoolIdsFromExisting,
    });
    updateSelection(centreIds, schoolIdsFromExisting);
  }, [isNew, form, cancellationFeeProduct?.id, defaultPublishedPricingYear]);

  useEffect(() => {
    if (isNew) {
      setInitialExcludedSystems([]);
      setExcludedSystemIds([]);
      setIsExclusionLoading(false);
      return;
    }

    const productId = cancellationFeeProduct?.id ?? 0;
    if (!productId) {
      setInitialExcludedSystems([]);
      setExcludedSystemIds([]);
      setIsExclusionLoading(false);
      return;
    }

    let cancelled = false;
    setIsExclusionLoading(true);
    api
      .getExcludedBookingSystemIdsForProduct(productId, CANCELLATION_FEE_PRODUCT_TYPE_ID)
      .then(ids => {
        if (!cancelled) {
          setInitialExcludedSystems(ids);
          setExcludedSystemIds(ids);
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setInitialExcludedSystems([]);
          setExcludedSystemIds([]);
          message.error(err?.message ?? 'Failed to load booking system exclusions');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsExclusionLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isNew, cancellationFeeProduct?.id, form]);

  useEffect(() => {
    if (!publishedPricingYears.length) return;
    const current = Number(form.getFieldValue('pricingYear'));
    const currentIsPublished = publishedPricingYears.some(py => py.year === current);
    if (currentIsPublished) return;
    if (defaultPublishedPricingYear != null) {
      form.setFieldsValue({ pricingYear: defaultPublishedPricingYear });
    }
  }, [publishedPricingYears, defaultPublishedPricingYear, form]);

  useEffect(() => {
    if (formErrors?.length) {
      formErrors.forEach(err => {
        form.setFields([{ name: err.name, errors: [err.error] }]);
      });
    }
  }, [formErrors, form]);

  const handleValuesChange = (_changed: any, all: any) => {
    const base: Partial<CancellationFee> = isNew ? {} : (draftProduct || cancellationFeeProduct || {});
    dispatch(UpdateDraftCancellationFeeProduct({ ...(base as CancellationFee), ...all }));
  };

  const handleSubmit = async () => {
    try {
      dispatch(setFormErrors([]));

      if (!divisionId) {
        message.error('Please select a division before saving.');
        setStatusMsg('Division is required');
        setStatusIsError(true);
        return;
      }

      const values = await form.validateFields();
      setLoading(true);
      onSaveStageChange?.('saving-cancellation-fee');

      const existing = draftProduct ?? cancellationFeeProduct;
      const accountCategoryId = Number(values.accountCategoryId);
      const productCategoryId = Number(values.productCategoryId);
      const accountCategoryText = accountCategories.find(c => c.id === accountCategoryId)?.name ?? '';
      const productCategoryText = productCategories.find(c => c.id === productCategoryId)?.name ?? '';
      const centreIds = selectedCentreIds;
      const schoolIds = selectedSchoolIds;
      const normalizedSelected = excludedSystemIds;

      const payload: Partial<CancellationFee> = {
        id: isNew ? 0 : Number(existing?.id ?? 0),
        name: String(values.name ?? existing?.name ?? '').trim(),
        divisionId: existing?.divisionId || divisionId,
        accountCategoryId,
        accountCategoryText,
        productCategoryId,
        productCategoryText,
        isPublished: values.active !== false,
        active: values.active !== false,
        centreIds: Array.isArray(centreIds) ? centreIds : [],
        schoolIds: Array.isArray(schoolIds) ? schoolIds : [],
        schools: [],
      };

      const result = await api.saveCancellationFee(payload, divisionId);
      if (!result.ok) {
        message.error(result.message ?? 'Failed to save cancellation fee');
        setStatusMsg('Error: ' + (result.message ?? 'Unknown error'));
        setStatusIsError(true);
        return;
      }
      if (result.errors?.length) {
        message.error(result.errors.join('; '));
        setStatusMsg('Error');
        setStatusIsError(true);
        return;
      }

      const savedProductId = result.data?.id ?? payload.id ?? cancellationFeeProduct?.id ?? 0;

      if (savedProductId > 0) {
        try {
          const updatedIds = await api.updateBookingSystemProductExclusions(
            savedProductId,
            CANCELLATION_FEE_PRODUCT_TYPE_ID,
            normalizedSelected,
            initialExcludedSystems
          );
          setInitialExcludedSystems(updatedIds);
        } catch (err: any) {
          const msg = err?.message || 'Failed to update booking system exclusions';
          message.error(msg);
          setStatusMsg('Error ' + msg);
          setStatusIsError(true);
          return;
        }
      } else if (normalizedSelected.length > 0) {
        message.warning('Saved, but booking system exclusions could not be updated because the product id was unavailable.');
      }

      const pricingYear = Number(values.pricingYear);
      const pricingYearRecord = publishedPricingYears.find(py => py.year === pricingYear);
      if (
        !Number.isInteger(pricingYear) ||
        pricingYear <= 0 ||
        !pricingYearRecord
      ) {
        message.error('Please select a published pricing year.');
        setStatusMsg('A published pricing year is required');
        setStatusIsError(true);
        return;
      }

      if (savedProductId > 0) {
        onSaveStageChange?.('saving-offering');
        const offeringResult = await api.syncCancellationFeeOffering(
          { ...payload, schoolIds },
          savedProductId,
          pricingYear,
          pricingYearRecord.id,
          divisionId,
          isNew
        );
        if (!offeringResult.ok) {
          const offeringError = offeringResult.message ?? 'Failed to sync cancellation fee offering';
          message.error(offeringError);
          setStatusMsg('Error: ' + offeringError);
          setStatusIsError(true);
          return;
        }
      }

      onSaveStageChange?.('completed');
      await new Promise(resolve => setTimeout(resolve, COMPLETED_DISPLAY_MS));

      message.success(isNew ? 'Cancellation fee created!' : 'Cancellation fee updated!');
      setStatusMsg(isNew ? 'Cancellation fee created' : 'Cancellation fee updated');
      setStatusIsError(false);

      dispatch(UpdateDraftCancellationFeeProduct(undefined));
      dispatch(loadDraftCancellationFeeProduct(undefined));
      if (isNew) {
        form.resetFields();
        form.setFieldsValue({ active: true });
        setInitialExcludedSystems([]);
        setExcludedSystemIds([]);
      }

      if (onSuccess) await onSuccess();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.message ?? 'Failed to save cancellation fee');
      setStatusMsg('Error: ' + (err?.message ?? 'Unknown'));
      setStatusIsError(true);
    } finally {
      setLoading(false);
      onSaveStageChange?.('idle');
    }
  };

  if (!isNew && !cancellationFeeProduct && !draftProduct) {
    return <div>No cancellation fee found</div>;
  }

  return (
    <Form
      layout="vertical"
      form={form}
      disabled={loading}
      initialValues={{ active: true }}
      onValuesChange={handleValuesChange}
      onFinish={handleSubmit}
    >
      {!divisionId && (
        <Alert
          type="warning"
          title="Division required"
          description="Please select a division from the header before creating or editing cancellation fees."
          showIcon
          className="cancellation-fees-alert"
        />
      )}
      <Form.Item
        label="Name"
        name="name"
        rules={[
          { required: true, message: 'Name is required' },
          { max: 100, message: 'Max length is 100 characters' },
          {
            validator: async (_rule, value) => {
              if (!value) return Promise.resolve();
              const existingFee = (cancellationFeeProducts ?? []).find(
                f => f.name?.toLowerCase() === value.toLowerCase() && f.id !== cancellationFeeProduct?.id
              );
              if (existingFee) {
                return Promise.reject(new Error('Name should be unique'));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input maxLength={100} placeholder="Enter cancellation fee" disabled={!divisionId} />
      </Form.Item>

      <Form.Item hidden label="ID" name="id">
        <Input disabled />
      </Form.Item>

      <Form.Item
        label="Account Category"
        name="accountCategoryId"
        rules={[{ required: true, message: 'Account Category is required' }]}
      >
        <Select
          placeholder="Select Account Category"
          options={accountCategories.map(c => ({ label: c.name, value: c.id }))}
          allowClear
          showSearch={{ optionFilterProp: 'label' }}
          disabled={!divisionId}
        />
      </Form.Item>

      <Form.Item
        label="Product Category"
        name="productCategoryId"
        rules={[{ required: true, message: 'Product Category is required' }]}
      >
        <Select
          placeholder="Select Product Category"
          options={productCategories.map(c => ({ label: c.name, value: c.id }))}
          allowClear
          showSearch={{ optionFilterProp: 'label' }}
          disabled={!divisionId}
        />
      </Form.Item>

      <Form.Item
        label="Pricing Year"
        name="pricingYear"
        rules={[{ required: true, message: 'Pricing Year is required' }]}
      >
        <Select
          placeholder="Select Pricing Year"
          options={publishedPricingYears.map(py => ({ label: String(py.year), value: py.year }))}
          allowClear
          showSearch={{ optionFilterProp: 'label' }}
          disabled={!divisionId || publishedPricingYears.length === 0}
        />
      </Form.Item>

      <ActiveStatusFormItem name="active" disabled={!divisionId} />

      <Form.Item label="Exclude this fee product from the following systems:">
        <Checkbox.Group
          className="cancellation-fees-checkbox-group"
          value={excludedSystemIds}
          disabled={isExclusionLoading}
          onChange={values => {
            setExcludedSystemIds((values as (string | number)[]).map(id => String(id)));
          }}
        >
          {bookingSystems.map(system => (
            <Checkbox key={system.id} value={String(system.id)}>
              {system.name}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Form.Item>

      <Form.Item label="Applies To">
        <div className="cancellation-fees-applies-to">
          <Collapse ghost bordered={false}>
            {Object.entries(centresByCountry).map(([countryName, countryCentres]) => {
                const countrySelectedCount = countryCentres.reduce((count, centre) => {
                  const centreSelected = selectedCentreIds.some(id => centreKeysMatch(id, centre.id));
                  if (centreSelected) {
                    return count + 1;
                  }
                  const schoolIds = getSchoolIdsForCentre(centre.id);
                  const selectedSchoolCount = selectedSchoolIds.filter(id => schoolIds.some(sid => schoolKeysMatch(sid, id))).length;
                  return count + (selectedSchoolCount > 0 ? 1 : 0);
                }, 0);

                const summary = countrySelectedCount ? `${countrySelectedCount} selected` : undefined;
                const allCentreIds = countryCentres.map(c => c.id);
                const countryChecked = countrySelectedCount === countryCentres.length && countryCentres.length > 0;
                const countryIndeterminate = countrySelectedCount > 0 && countrySelectedCount < countryCentres.length;
                return (
                  <Collapse.Panel
                    key={countryName}
                    header={
                      <Space onClick={e => e.stopPropagation()}>
                        <Checkbox
                          checked={countryChecked}
                          indeterminate={countryIndeterminate}
                          onChange={e => setCountrySelection(allCentreIds, e.target.checked)}
                          onClick={e => e.stopPropagation()}
                          style={{ marginTop: 6 }}
                          aria-label={`Select all centres in ${countryName}`}
                        />
                        <span>{countryName}</span>
                        {summary && <Typography.Text type="secondary">{summary}</Typography.Text>}
                      </Space>
                    }
                  >
                    <Space orientation="vertical" size={16} style={{ width: '100%', paddingLeft: 24 }}>
                      {countryCentres.map(centre => {
                        const centreSchoolIds = getSchoolIdsForCentre(centre.id);
                        const centreChecked = selectedCentreIds.some(id => centreKeysMatch(id, centre.id));
                        const selectedSchoolIdsForCentre = centreSchoolIds.filter(id =>
                          selectedSchoolIds.some(selected => schoolKeysMatch(selected, id))
                        );
                        const centreIndeterminate = !centreChecked && selectedSchoolIdsForCentre.length > 0;

                        return (
                          <div key={centre.id} style={{ marginBottom: 16 }}>
                            <Checkbox
                              checked={centreChecked}
                              indeterminate={centreIndeterminate}
                              disabled={!divisionId}
                              onChange={e => handleCentreToggle(centre.id, e.target.checked)}
                            >
                              {centre.name}
                            </Checkbox>
                            {centreSchoolIds.length > 0 && (
                              <div style={{ marginLeft: 24, marginTop: 8 }}>
                                {centreSchoolIds.map(schoolId => {
                                  const school = schools.find(s => schoolKeysMatch(s.id ?? '', schoolId));
                                  if (!school) return null;
                                  const schoolChecked = selectedCentreIds.some(id => centreKeysMatch(id, centre.id)) ||
                                    selectedSchoolIds.some(id => schoolKeysMatch(id, school.id ?? ''));
                                  return (
                                    <div key={school.id} style={{ marginBottom: 6 }}>
                                      <Checkbox
                                        checked={schoolChecked}
                                        disabled={!divisionId}
                                        onChange={e => handleSchoolToggle(centre.id, school.id ?? '', e.target.checked)}
                                      >
                                        {school.name}
                                      </Checkbox>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </Space>
                  </Collapse.Panel>
                );
              })}
          </Collapse>
        </div>
      </Form.Item>

      <Form.Item className="cancellation-fees-form-actions">
        <Button
          type="primary"
          htmlType="submit"
          disabled={!divisionId || loading}
          className="cancellation-fees-save-btn"
        >
          Save
        </Button>
        <span className={`cancellation-fees-status-msg ${statusIsError ? 'cancellation-fees-status-error' : ''}`}>{statusMsg}</span>
      </Form.Item>
    </Form>
  );
};

export default AddEditCancellationFeeForm;